import { message } from 'ant-design-vue';
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons-vue';
import { h, isVNode } from 'vue';
import './save_result_toast.less';
import {
  SAVE_RESULT_SUCCESS_KEY,
  markSuccessToastShown,
  shouldShowSuccessToast,
} from './save_result_toast_state';

export {
  SAVE_RESULT_SUCCESS_DURATION_MS,
  SAVE_RESULT_SUCCESS_KEY,
  markSuccessToastShown,
  resetSaveResultToastState,
  shouldShowSuccessToast,
} from './save_result_toast_state';

let installed = false;

type MessageCaller = typeof message.success;

function isConfigObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !isVNode(value) && 'content' in (value as object);
}

function toConfig(
  type: 'success' | 'error',
  content: unknown,
  duration?: unknown,
  onClose?: unknown
): Record<string, unknown> {
  if (isConfigObject(content)) {
    return { ...content };
  }
  const durationValue = typeof duration === 'number' ? duration : 3;
  const closeHandler = typeof duration === 'function' ? duration : onClose;
  return {
    content,
    duration: durationValue,
    onClose: closeHandler,
  };
}

function wrapSuccess(raw: MessageCaller): MessageCaller {
  return ((content: unknown, duration?: unknown, onClose?: unknown) => {
    const now = Date.now();
    if (!shouldShowSuccessToast(now)) {
      return;
    }
    markSuccessToastShown(now);
    const config = toConfig('success', content, duration, onClose);
    return raw({
      ...config,
      duration: config.duration ?? 3,
      key: config.key ?? SAVE_RESULT_SUCCESS_KEY,
      class: ['save-result-toast', 'save-result-toast--success', config.class]
        .flat()
        .filter(Boolean)
        .join(' '),
      icon: config.icon ?? h(CheckCircleFilled),
    } as any);
  }) as MessageCaller;
}

function wrapError(raw: MessageCaller): MessageCaller {
  return ((content: unknown, duration?: unknown, onClose?: unknown) => {
    const config = toConfig('error', content, duration, onClose);
    return raw({
      ...config,
      duration: config.duration ?? 3,
      key: config.key ?? `jeecg-save-result-error-${Date.now()}`,
      class: ['save-result-toast', 'save-result-toast--error', config.class]
        .flat()
        .filter(Boolean)
        .join(' '),
      icon: config.icon ?? h(ExclamationCircleFilled),
    } as any);
  }) as MessageCaller;
}

export function installSaveResultToast() {
  if (installed) {
    return;
  }
  installed = true;
  message.success = wrapSuccess(message.success.bind(message));
  message.error = wrapError(message.error.bind(message));
}

installSaveResultToast();
